import type { CarrierFilterItem, CarrierWithPrice } from '@optionway/models';
import { PARAM_LEGS_UNSELECTED_AIRLINES } from '#shared/query-parameters';

interface AirlinesFilterStore {
  deselectedIataCodes: string[] | undefined;
}

const AIRLINES_FILTER_DEFAULTS: AirlinesFilterStore = {
  deselectedIataCodes: [],
};

export const useAirlinesFilterStore = defineStore('filters/airlines', {
  state: (): AirlinesFilterStore => ({
    deselectedIataCodes: AIRLINES_FILTER_DEFAULTS.deselectedIataCodes,
  }),
  getters: {
    // Only cross-store access allowed in this store: useSearchResultIndexStore().allFilterOptions — read-only,
    // no other getter, no other store, no actions.
    allCarriers(): CarrierWithPrice[] {
      const resultsStore = useSearchResultIndexStore();
      const items: CarrierWithPrice[] = [];
      for (const carrier of resultsStore.allFilterOptions.carriers ?? []) {
        if (!carrier.iataCode) continue;
        items.push({
          iataCode: carrier.iataCode,
          name: carrier.name ?? carrier.iataCode,
          price: carrier.price,
        });
      }
      return items.toSorted((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    },
    // The backend price travels untouched (Data ownership across the BFF boundary): the price the panel
    // displays is the results index's filteredCarriers, chosen by the view.
    airlines(): CarrierFilterItem[] {
      const deselected = new Set(getDeselectedIataCodes(this.deselectedIataCodes));
      return this.allCarriers.map((c) => ({ ...c, selected: !deselected.has(c.iataCode ?? '') }));
    },
    allSelected(): boolean {
      return getDeselectedIataCodes(this.deselectedIataCodes).length === 0;
    },
    hasActiveFilters(): boolean {
      return getDeselectedIataCodes(this.deselectedIataCodes).length > 0;
    },
    filterQueryString(): string {
      if (!this.hasActiveFilters) return '';
      const parameters = new URLSearchParams();
      parameters.set(PARAM_LEGS_UNSELECTED_AIRLINES, (this.deselectedIataCodes ?? []).join(','));
      return parameters.toString();
    },
    // All carriers deselected: the allowed list would be empty, guaranteed 0 results.
    isImpossible(): boolean {
      return !this.allSelected && this.airlines.every((a) => !a.selected);
    },
    allowedAirlines(): string | undefined {
      if (this.allSelected) return undefined;
      const deselected = new Set(getDeselectedIataCodes(this.deselectedIataCodes));
      return this.allCarriers.flatMap((c) => (c.iataCode && !deselected.has(c.iataCode) ? [c.iataCode] : [])).join(',');
    },
  },
  actions: {
    // unselectedAirlines is a comma-separated list of IATA codes to deselect, e.g. "AF,U2".
    applyFromUrlQuery(url: string) {
      this.$reset();
      const parameters = new URL(url, 'https://dummy.com').searchParams;
      const unselected = parameters.get(PARAM_LEGS_UNSELECTED_AIRLINES);
      if (unselected !== null) this.deselectedIataCodes = parseIataCodeList(unselected);
    },
    toggleAll() {
      this.deselectedIataCodes = this.allSelected
        ? this.allCarriers.flatMap((c) => (c.iataCode ? [c.iataCode] : []))
        : [];
    },
    toggle(iataCode: string, value: boolean) {
      const deselectedIataCodes = getDeselectedIataCodes(this.deselectedIataCodes);
      this.deselectedIataCodes = value
        ? deselectedIataCodes.filter((c) => c !== iataCode)
        : [...deselectedIataCodes, iataCode];
    },
    solo(iataCode: string) {
      this.deselectedIataCodes = this.allCarriers.flatMap((c) =>
        c.iataCode && c.iataCode !== iataCode ? [c.iataCode] : [],
      );
    },
  },
});

function getDeselectedIataCodes(deselectedIataCodes: string[] | undefined): string[] {
  return deselectedIataCodes ?? [];
}
